import { z } from 'zod';
import { totalDeFactura } from './receipts';
import type { DailyRevenue, ExpenseEntry } from './types';

/**
 * Cuadre por mes con el sistema del cliente (Kilo, oct 2026).
 *
 * Una organizacion con ERP no se fia de la P&L ni del punto de equilibrio
 * hasta comprobar que «enero en Alexia» es «enero en su sistema». Kilo
 * comparo ventas con Restaurant.pe, vio diferencias y no supo si venian de
 * los impuestos, de una carga incompleta o del mapeo.
 *
 * Esto pone, mes a mes, lo que Alexia tiene (dias cubiertos, huecos, total
 * sin y con impuestos) al lado del total que el usuario apunta de su sistema,
 * y dice la diferencia. No adivina la causa: da los tres datos que la
 * explican casi siempre (dias que faltan, con o sin IVA, cuantos registros).
 *
 * El total de la fuente lo apunta el usuario y se guarda: es la prueba de que
 * ese mes se comprobo, y deja de cuadrar sola si alguien toca el mes despues.
 */

export const CUADRE_KINDS = ['ventas', 'gastos'] as const;
export type CuadreKind = (typeof CUADRE_KINDS)[number];

/** Clave del consolidado de todos los locales. */
export const CUADRE_TODOS = 'todos';

/** Diferencia que se da por cuadrada: redondeos de centimos entre sistemas. */
export const TOLERANCIA_CUADRE = 1;

export interface CuadreFuente {
  kind: CuadreKind;
  month: string;
  /** Local, o `CUADRE_TODOS` para el consolidado. */
  scope: string;
  sourceTotal: number;
  /** ¿El total apuntado lleva impuestos? Decide con que total se compara. */
  includesVat: boolean;
  /** De donde sale: «Restaurant.pe», «ERP», «Registro SUNAT»… */
  sourceName?: string;
  /** «Dejarlo así» en la tarea del Panel: no cuadra, pero ya lo revisó. */
  accepted?: boolean;
  updatedAt: string;
}

export const cuadreFuenteInputSchema = z.object({
  sourceTotal: z.number().finite().min(0, 'El total no puede ser negativo'),
  includesVat: z.boolean(),
  sourceName: z.string().trim().max(60).optional(),
  accepted: z.boolean().optional(),
});
export type CuadreFuenteInput = z.infer<typeof cuadreFuenteInputSchema>;

export type CuadreEstado = 'sin_fuente' | 'cuadra' | 'no_cuadra';

export interface CuadreMes {
  month: string;
  /** Dias del mes que cuentan (hasta hoy si es el mes en curso). */
  diasDelMes: number;
  /** Ventas: dias con importe. Gastos: dias con algun documento. */
  diasConDatos: number;
  /** Solo ventas: dias sin importe, para ver huecos de una carga. */
  diasSinDatos: string[];
  registros: number;
  totalNeto: number;
  totalConIva: number;
  fuente?: CuadreFuente;
  /** Alexia − fuente, con el total comparable (con o sin IVA). */
  diferencia?: number;
  estado: CuadreEstado;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

function diasDe(month: string, hoy: string): string[] {
  const [y, m] = month.split('-').map(Number);
  const ultimo = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const dias: string[] = [];
  for (let d = 1; d <= ultimo; d++) {
    const fecha = `${month}-${String(d).padStart(2, '0')}`;
    if (fecha > hoy) break;
    dias.push(fecha);
  }
  return dias;
}

function comparar(
  base: Omit<CuadreMes, 'fuente' | 'diferencia' | 'estado'>,
  fuente: CuadreFuente | undefined,
): CuadreMes {
  if (!fuente) return { ...base, estado: 'sin_fuente' };
  const alexia = fuente.includesVat ? base.totalConIva : base.totalNeto;
  const diferencia = round2(alexia - fuente.sourceTotal);
  return {
    ...base,
    fuente,
    diferencia,
    estado: Math.abs(diferencia) <= TOLERANCIA_CUADRE ? 'cuadra' : 'no_cuadra',
  };
}

const fuenteDe = (fuentes: CuadreFuente[], kind: CuadreKind, month: string, scope: string) =>
  fuentes.find((f) => f.kind === kind && f.month === month && f.scope === scope);

/** Ventas netas de un dia: lo mismo que suma la P&L. */
export function ventasDelDia(day: Pick<DailyRevenue, 'inHouse' | 'delivery'>): number {
  return (
    Object.values(day.inHouse ?? {}).reduce((a, b) => a + (b ?? 0), 0) +
    Object.values(day.delivery ?? {}).reduce((a, b) => a + (b ?? 0), 0)
  );
}

/**
 * Las ventas se guardan sin IVA; el total con IVA se reconstruye con el tipo
 * de los ajustes. Es una estimacion (un TPV con varios tipos no cuadrara al
 * centimo) y la pantalla lo dice.
 */
export function cuadreVentas(
  revenues: Array<Pick<DailyRevenue, 'date' | 'inHouse' | 'delivery'>>,
  months: string[],
  vatPct: number,
  fuentes: CuadreFuente[],
  scope: string,
  hoy: string,
): CuadreMes[] {
  return months.map((month) => {
    const delMes = revenues.filter((r) => r.date.startsWith(month));
    const porDia = new Map<string, number>();
    for (const r of delMes) porDia.set(r.date, (porDia.get(r.date) ?? 0) + ventasDelDia(r));
    const dias = diasDe(month, hoy);
    const totalNeto = round2([...porDia.values()].reduce((a, b) => a + b, 0));
    return comparar(
      {
        month,
        diasDelMes: dias.length,
        diasConDatos: dias.filter((d) => (porDia.get(d) ?? 0) !== 0).length,
        diasSinDatos: dias.filter((d) => (porDia.get(d) ?? 0) === 0),
        registros: delMes.length,
        totalNeto,
        totalConIva: round2(totalNeto * (1 + vatPct / 100)),
      },
      fuenteDe(fuentes, 'ventas', month, scope),
    );
  });
}

/**
 * Compras del mes por fecha de documento. Los albaranes no cuentan (igual que
 * en la P&L: los cubre su factura) y el total con impuestos es el de cada
 * factura, no un porcentaje: aqui si se conoce documento a documento.
 */
export function cuadreGastos(
  expenses: Array<
    Pick<ExpenseEntry, 'date' | 'netAmount' | 'vatPct' | 'taxAmount' | 'invoiceTotal' | 'vatBreakdown' | 'documentType'>
  >,
  months: string[],
  fuentes: CuadreFuente[],
  scope: string,
  hoy: string,
): CuadreMes[] {
  const facturas = expenses.filter((e) => (e.documentType ?? 'factura') === 'factura');
  return months.map((month) => {
    const delMes = facturas.filter((e) => e.date.startsWith(month));
    return comparar(
      {
        month,
        diasDelMes: diasDe(month, hoy).length,
        diasConDatos: new Set(delMes.map((e) => e.date)).size,
        diasSinDatos: [],
        registros: delMes.length,
        totalNeto: round2(delMes.reduce((a, e) => a + e.netAmount, 0)),
        totalConIva: round2(delMes.reduce((a, e) => a + totalDeFactura(e), 0)),
      },
      fuenteDe(fuentes, 'gastos', month, scope),
    );
  });
}

/**
 * Por que puede no cuadrar, en frases. Solo las causas que se pueden leer en
 * los datos; si no sale ninguna, la pantalla dice que hay que mirar el mes a
 * mano. `impuesto` es el nombre local: IVA, IGV, INC…
 */
export function pistasCuadre(mes: CuadreMes, impuesto: string): string[] {
  if (mes.estado !== 'no_cuadra' || !mes.fuente) return [];
  const pistas: string[] = [];
  const { fuente } = mes;
  const otroTotal = fuente.includesVat ? mes.totalNeto : mes.totalConIva;
  if (Math.abs(otroTotal - fuente.sourceTotal) <= Math.max(TOLERANCIA_CUADRE, fuente.sourceTotal * 0.005)) {
    pistas.push(
      fuente.includesVat
        ? `Tu total coincide con el de Alexia sin ${impuesto}: ¿seguro que lo lleva incluido?`
        : `Tu total coincide con el de Alexia con ${impuesto}: parece que lo lleva incluido.`,
    );
  }
  if (mes.diasSinDatos.length > 0 && (mes.diferencia ?? 0) < 0) {
    pistas.push(
      `Alexia tiene menos y le ${mes.diasSinDatos.length === 1 ? 'falta 1 día' : `faltan ${mes.diasSinDatos.length} días`} de ventas: si abriste esos días, la carga está incompleta.`,
    );
  }
  return pistas;
}
